import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Building2, Zap, RefreshCw, AlertTriangle, StopCircle, Sparkles, XCircle, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ENRICH_MESSAGES = [
  "🕵️ Snooping through LinkedIn like a pro...",
  "🔮 Consulting the crystal ball for contact data...",
  "🐕 Sniffing out email addresses like a bloodhound...",
  "🎣 Fishing for phone numbers in the data ocean...",
  "🔬 Running forensic analysis on digital footprints...",
  "🧙 Casting a contact-finding spell...",
  "🚀 Launching the enrichment rockets...",
  "🎯 Zeroing in on decision maker intel...",
  "🦊 Being sly and resourceful finding contacts...",
  "☕ Grabbing a coffee while the data flows in...",
  "🧲 Magnetically attracting contact details...",
  "🌊 Surfing the web for hidden email addresses...",
  "🏴‍☠️ Plundering the seven seas of data...",
  "🤖 Deploying enrichment bots... beep boop...",
  "🎰 Spinning the enrichment wheel of fortune...",
  "🪄 Abracadabra... give me that email!",
  "🐝 Buzzing through databases like a busy bee...",
  "🕸️ Weaving through the web of connections...",
  "🧃 Squeezing out every last drop of data...",
  "💎 Mining for contact diamonds in the rough...",
];

export function CompanyIntelligenceCard() {
  const [stats, setStats] = useState(null);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState('unresearched');

  // Bulk enrich state
  const [enriching, setEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState({ current: 0, total: 0, companyName: '', dmName: '', message: '', found: 0, failed: 0 });
  const enrichCancelRef = useRef(false);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, statusRes] = await Promise.all([
        axios.get(`${API}/api/companies/research/stats`, { withCredentials: true }),
        axios.get(`${API}/api/companies/research/bulk/status`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setBulkStatus(statusRes.data);
      if (statusRes.data?.is_running) {
        setRunning(true);
        setTimeout(fetchStats, 5000);
      } else {
        setRunning(false);
      }
    } catch (e) {
      console.error('Failed to load research stats', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const startBulk = async () => {
    setRunning(true);
    try {
      const res = await axios.post(
        `${API}/api/companies/research/bulk?mode=${mode}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Research started for ${res.data.queued} companies`);
      setTimeout(fetchStats, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start bulk research');
      setRunning(false);
    }
  };

  const cancelBulk = async () => {
    try {
      await axios.post(`${API}/api/companies/research/bulk/cancel`, {}, { withCredentials: true });
      toast.success('Research cancelled');
      setRunning(false);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel research');
    }
  };

  // ── Bulk Enrich All DMs ──
  const startBulkEnrich = async () => {
    setEnriching(true);
    enrichCancelRef.current = false;
    setEnrichProgress({ current: 0, total: 0, companyName: '', dmName: '', message: ENRICH_MESSAGES[0], found: 0, failed: 0, pulse: 0, apollo: 0 });

    try {
      const res = await axios.get(`${API}/api/companies/research/unenriched-dms`, { withCredentials: true });
      const items = res.data.items || [];

      if (items.length === 0) {
        toast.info('✅ All decision makers already have contact data');
        setEnriching(false);
        return;
      }

      toast.success(`Found ${items.length} decision makers to enrich across ${new Set(items.map(i => i.company_id)).size} companies`);
      let found = 0;
      let failed = 0;
      let pulseCount = 0;
      let apolloCount = 0;

      for (let i = 0; i < items.length; i++) {
        if (enrichCancelRef.current) {
          toast.info(`🛑 Enrichment cancelled after ${i} of ${items.length}`);
          break;
        }

        const item = items[i];
        const msg = ENRICH_MESSAGES[i % ENRICH_MESSAGES.length];
        setEnrichProgress({
          current: i + 1,
          total: items.length,
          companyName: item.company_name,
          dmName: item.dm_name,
          message: msg,
          found,
          failed,
          pulse: pulseCount,
          apollo: apolloCount,
        });

        try {
          const enrichRes = await axios.post(
            `${API}/api/companies/${item.company_id}/research/decision-makers/${item.dm_id}/enrich`,
            {},
            { withCredentials: true }
          );
          if (enrichRes.data.status === 'enriched' && (enrichRes.data.updates?.email || enrichRes.data.updates?.phone)) {
            found++;
            const src = enrichRes.data.updates?.enrich_source;
            if (src === 'apollo') apolloCount++;
            else pulseCount++;
          } else {
            failed++;
          }
        } catch {
          failed++;
        }
      }

      setEnrichProgress(prev => ({ ...prev, current: prev.total, found, failed, pulse: pulseCount, apollo: apolloCount, dmName: '', companyName: '' }));

      if (!enrichCancelRef.current) {
        const parts = [];
        if (pulseCount > 0) parts.push(`${pulseCount} from Pulse`);
        if (apolloCount > 0) parts.push(`${apolloCount} from Apollo`);
        toast.success(`🎉 Done! Enriched ${found} of ${items.length} — ${parts.join(', ')}${failed > 0 ? ` (${failed} no data)` : ''}`);
      }
    } catch (e) {
      toast.error('Failed to fetch unenriched decision makers');
    }

    setEnriching(false);
  };

  const cancelEnrich = () => {
    enrichCancelRef.current = true;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center justify-center text-gray-400 text-sm gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading research stats...
        </CardContent>
      </Card>
    );
  }

  const coveragePct = stats?.coverage_pct || 0;
  const enrichPct = enrichProgress.total > 0 ? Math.round((enrichProgress.current / enrichProgress.total) * 100) : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="w-5 h-5 text-blue-600" />
          Company Intelligence
        </CardTitle>
        <p className="text-sm text-gray-500 mt-0.5">
          Enrich company records with decision makers, tech stack, hiring velocity, Glassdoor data, news, ERS scoring — plus auto-enrich DM contact info (email &amp; phone).
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Coverage bar */}
        {stats && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Research Coverage</span>
              <span className="font-bold text-gray-800">{coveragePct}%</span>
            </div>
            <Progress value={coveragePct} className="h-2" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: 'Total',        value: stats.total,        color: 'text-gray-700' },
                { label: 'Researched',   value: stats.researched,   color: 'text-green-700' },
                { label: 'Unresearched', value: stats.unresearched, color: 'text-orange-700' },
                { label: 'Stale (60d+)', value: stats.stale_60d,    color: 'text-red-600' },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center p-2 bg-gray-50 rounded-lg">
                  <p className={`text-xl font-bold ${color}`}>{value ?? 0}</p>
                  <p className="text-xs text-gray-400">{label}</p>
                </div>
              ))}
            </div>
            {(stats.hot_count > 0 || stats.warm_count > 0) && (
              <div className="flex gap-2 flex-wrap">
                <Badge className="bg-red-100 text-red-800 border-0 gap-1">🔥 {stats.hot_count} HOT</Badge>
                <Badge className="bg-orange-100 text-orange-800 border-0 gap-1">🌡 {stats.warm_count} WARM</Badge>
              </div>
            )}
          </div>
        )}

        {/* Active run status */}
        {running && bulkStatus?.is_running ? (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 text-sm text-blue-800 font-medium">
                  <RefreshCw className="w-4 h-4 animate-spin shrink-0" />
                  Research in progress...
                </div>
                {bulkStatus?.current_companies?.length > 0 && (
                  <div className="flex flex-col gap-0.5 pl-6">
                    {bulkStatus.current_companies.map((name) => (
                      <span key={name} className="text-xs text-blue-700 truncate">↳ {name}</span>
                    ))}
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelBulk}
                className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                <StopCircle className="w-3.5 h-3.5" />
                Cancel
              </Button>
            </div>
            <Progress className="h-1.5" />
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div><p className="font-bold text-blue-700 text-lg">{bulkStatus.queued}</p><p className="text-gray-500">Queued</p></div>
              <div><p className="font-bold text-yellow-700 text-lg">{bulkStatus.in_progress}</p><p className="text-gray-500">Running</p></div>
              <div><p className="font-bold text-green-700 text-lg">{bulkStatus.complete}</p><p className="text-gray-500">Done</p></div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Mode selector */}
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block font-medium">Research Mode</label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 'unresearched', label: 'Unresearched Only' },
                  { value: 'stale',        label: 'Refresh Stale (60d+)' },
                  { value: 'all',          label: 'Re-run All' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setMode(opt.value)}
                    className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                      mode === opt.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={startBulk}
              disabled={running || enriching || (mode === 'unresearched' && (stats?.unresearched || 0) === 0)}
              className="w-full gap-2"
            >
              <Zap className="w-4 h-4" />
              {mode === 'unresearched'
                ? `Start Research — ${stats?.unresearched || 0} Unresearched Companies`
                : mode === 'stale'
                ? `Start Refresh — ${stats?.stale_60d || 0} Stale Companies`
                : `Re-run All — ${stats?.total || 0} Companies`}
            </Button>

            {mode === 'unresearched' && (stats?.unresearched || 0) === 0 && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                ✅ All companies have been researched.
              </p>
            )}

            {mode === 'all' && (
              <p className="text-xs text-orange-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                This will re-research all companies and may use significant Apify credits.
              </p>
            )}
          </div>
        )}

        {/* ── Bulk Enrich DMs Section ── */}
        <div className="border-t border-gray-100 pt-4">
          {enriching ? (
            <div className="rounded-xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />
                  <span className="text-sm font-bold text-purple-800">
                    Enriching {enrichProgress.current} of {enrichProgress.total}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs h-7 px-3 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={cancelEnrich}
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Cancel
                </Button>
              </div>

              {/* Progress bar */}
              <div className="w-full h-2 bg-purple-100 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${enrichPct}%` }}
                />
              </div>

              {/* Current company + DM */}
              {enrichProgress.companyName && (
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <span className="text-xs font-bold text-purple-700 truncate">{enrichProgress.companyName}</span>
                </div>
              )}
              {enrichProgress.dmName && (
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-500 shrink-0" />
                  <span className="text-sm font-semibold text-gray-700 truncate">{enrichProgress.dmName}</span>
                </div>
              )}

              {/* Funny message */}
              <p className="text-xs text-purple-600 font-medium italic mb-3">
                {enrichProgress.message}
              </p>

              {/* Stats */}
              <div className="flex gap-4 text-xs">
                <span className="text-blue-600 font-bold">🔵 {enrichProgress.pulse || 0} Pulse</span>
                <span className="text-orange-600 font-bold">🅰️ {enrichProgress.apollo || 0} Apollo</span>
                <span className="text-gray-400 font-medium">❌ {enrichProgress.failed} no data</span>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={startBulkEnrich}
              disabled={running || enriching}
              className="w-full gap-2 text-purple-700 border-purple-200 hover:bg-purple-50 hover:text-purple-800"
            >
              <Users className="w-4 h-4" />
              Enrich All Decision Makers
              <span className="text-xs text-purple-400 font-normal ml-1">— find missing emails &amp; phones</span>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
